import { useEffect, useRef } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";

const STORE = { lat: 50.9147, lng: -114.0719 };
const ZONE_COLORS = ["#0F4A23", "#2F7A3A", "#8CC47E"];

function ZoneLayer({ maxRadiusKm, zones, onRadius }) {
  const map = useMap();
  const store = useRef(null);
  const radiusCircle = useRef(null);
  const zoneCircles = useRef([]);

  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google.maps;
    store.current = new g.Marker({ position: STORE, map, title: "Bambam African Mart" });
    const circle = new g.Circle({
      map, center: STORE, radius: (maxRadiusKm || 1) * 1000,
      editable: true, draggable: false,
      strokeColor: "#C8202B", strokeWeight: 2, fillColor: "#C8202B", fillOpacity: 0.06,
    });
    radiusCircle.current = circle;
    circle.addListener("radius_changed", () => {
      onRadius(Math.max(1, Math.round(circle.getRadius() / 1000)));
    });
    map.fitBounds(circle.getBounds());
    return () => {
      circle.setMap(null);
      store.current?.setMap(null);
      zoneCircles.current.forEach((c) => c.setMap(null));
    };
    // eslint-disable-next-line
  }, [map]);

  // keep the editable radius in sync if changed elsewhere (e.g. via the number field)
  useEffect(() => {
    if (radiusCircle.current && Math.round(radiusCircle.current.getRadius() / 1000) !== maxRadiusKm) {
      radiusCircle.current.setRadius((maxRadiusKm || 1) * 1000);
    }
  }, [maxRadiusKm]);

  // redraw zone rings
  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google.maps;
    zoneCircles.current.forEach((c) => c.setMap(null));
    zoneCircles.current = [...zones]
      .sort((a, b) => (b.max_km || 0) - (a.max_km || 0))
      .map((z, i) => new g.Circle({
        map, center: STORE, radius: (z.max_km || 0) * 1000,
        clickable: false, editable: false,
        strokeColor: ZONE_COLORS[i % ZONE_COLORS.length], strokeWeight: 1.5, strokeOpacity: 0.7,
        fillColor: ZONE_COLORS[i % ZONE_COLORS.length], fillOpacity: 0.06,
      }));
    // eslint-disable-next-line
  }, [map, JSON.stringify(zones)]);

  return null;
}

export function DeliveryZoneMap({ maxRadiusKm, zones, onRadius }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return null;
  return (
    <div data-testid="delivery-google-map" className="overflow-hidden rounded-xl border border-border">
      <APIProvider apiKey={apiKey}>
        <Map
          defaultCenter={STORE}
          defaultZoom={11}
          style={{ width: "100%", height: "340px" }}
          gestureHandling="cooperative"
          disableDefaultUI={false}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
        >
          <ZoneLayer maxRadiusKm={maxRadiusKm} zones={zones} onRadius={onRadius} />
        </Map>
      </APIProvider>
    </div>
  );
}
